import {
  addChartBarsMs,
  CHART_TIMEFRAMES,
  chartBarPosition,
  chartBarStartMs,
  nextChartBarStartMs,
} from '@/lib/chart-timeframe'
import { ChartTimeframe } from '@/types/generated/common/types'
import type { OrderbookHeatmapColumn, OrderbookHeatmapRequest } from '@/types/generated/public_api'

/**
 * Timeframes the heatmap is drawn on: the chart grid from 1m up (the server rejects sub-minute).
 * MONTH_1 columns are calendar months, so column edges and request windows go through the
 * `lib/chart-timeframe` bar helpers, never `timeframe * 1000`.
 */
export const ORDERBOOK_HEATMAP_TIMEFRAMES: ReadonlySet<number> = new Set(
  CHART_TIMEFRAMES.map((t) => t.timeframe).filter(
    (timeframe) => timeframe >= ChartTimeframe.CHART_TIMEFRAME_MIN_1
  )
)

export const ORDERBOOK_HEATMAP_INDICATOR_ID = 'orderbookHeatmap'

/** The TradingView control study that gives the heatmap a legend entry (eye, remove). */
export const ORDERBOOK_HEATMAP_STUDY = {
  name: 'OrderX Order Book Heatmap',
  shortDescription: 'Order Book Heatmap',
} as const

/** Price band (± % of each snapshot's mid) the heatmap draws; the server sends ±25%. */
export const ORDERBOOK_HEATMAP_RANGE_OPTIONS = [1, 2, 5, 10, 25] as const
export type OrderbookHeatmapRange = (typeof ORDERBOOK_HEATMAP_RANGE_OPTIONS)[number]
export const DEFAULT_ORDERBOOK_HEATMAP_RANGE: OrderbookHeatmapRange = 5
/** Study/indicator input id for the band, shared by the TV study and the Percept control. */
export const ORDERBOOK_HEATMAP_RANGE_INPUT_ID = 'priceRangePct'

/** Accepts the input's raw value (TV select inputs come back as strings). */
export function parseOrderbookHeatmapRange(value: unknown): OrderbookHeatmapRange | undefined {
  const range = Number(value)
  return ORDERBOOK_HEATMAP_RANGE_OPTIONS.find((option) => option === range)
}

/**
 * Drops the levels outside ±`rangePct`% of the snapshot's mid. bids walk down from bidStart and
 * asks up from askStart, so the band is a prefix of each array.
 */
export function clipHeatmapColumn<T extends OrderbookHeatmapColumn>(
  column: T,
  rangePct: number
): T {
  const w = column.bucketWidth
  if (!(w > 0) || !(column.midPrice > 0) || rangePct >= 25) return column
  const low = column.midPrice * (1 - rangePct / 100)
  const high = column.midPrice * (1 + rangePct / 100)
  const bidCount = Math.max(0, Math.floor((column.bidStart - low) / w) + 1)
  const askCount = Math.max(0, Math.floor((high - column.askStart) / w) + 1)
  if (bidCount >= column.bids.length && askCount >= column.asks.length) return column
  return { ...column, bids: column.bids.slice(0, bidCount), asks: column.asks.slice(0, askCount) }
}

/**
 * Price grouping, as the order book widget groups its rows: each snapshot's buckets merge into
 * buckets `factor` times as wide and their sizes add up, bids and asks separately — Percept's
 * "Agg." heatmap. A multiple of the server's bucket rather than a price, since that bucket follows
 * the symbol's price ($50 for BTC, $1 for ETH) and an input's options are fixed. Input id shared by
 * the TV study and the Percept control, mirrored into chartOption (orderbookHeatmapGrouping).
 */
export const ORDERBOOK_HEATMAP_GROUPING_AUTO = 0
export const GROUPING_FACTORS = [1, 2, 5, 10, 20, 50] as const
export const ORDERBOOK_HEATMAP_GROUPING_OPTIONS = [
  ORDERBOOK_HEATMAP_GROUPING_AUTO,
  ...GROUPING_FACTORS,
] as const
export type OrderbookHeatmapGrouping = (typeof ORDERBOOK_HEATMAP_GROUPING_OPTIONS)[number]
/** Off by default: each server bucket is drawn as sent. Auto, when picked: the TV overlay picks
 *  the factor from the zoom (see autoHeatmapGrouping); Percept, which can't follow the zoom, uses
 *  its own HD tile (5 buckets). */
export const DEFAULT_ORDERBOOK_HEATMAP_GROUPING: OrderbookHeatmapGrouping = 1
export const PERCEPT_AUTO_GROUPING = 5
export const ORDERBOOK_HEATMAP_GROUPING_INPUT_ID = 'grouping'

/** Rows at least this tall (CSS px) read as separate tiles; thinner ones merge into lines. */
const AUTO_GROUPING_TARGET_PX = 9

/** The smallest factor whose grouped bucket is at least AUTO_GROUPING_TARGET_PX tall on screen. */
export function autoHeatmapGrouping(bucketWidth: number, pxPerPrice: number): number {
  if (!(bucketWidth > 0) || !(pxPerPrice > 0)) return 1
  return (
    GROUPING_FACTORS.find(
      (factor) => bucketWidth * factor * pxPerPrice >= AUTO_GROUPING_TARGET_PX
    ) ?? GROUPING_FACTORS[GROUPING_FACTORS.length - 1]
  )
}

export function parseOrderbookHeatmapGrouping(
  value: unknown
): OrderbookHeatmapGrouping | undefined {
  const grouping = Number(value)
  return ORDERBOOK_HEATMAP_GROUPING_OPTIONS.find((option) => option === grouping)
}

export function formatOrderbookHeatmapGrouping(grouping: number): string {
  if (grouping === ORDERBOOK_HEATMAP_GROUPING_AUTO) return 'Auto'
  return grouping === 1 ? 'Off' : `${grouping}× bucket`
}

/** Slack, in grouped buckets, for prices that land a hair under a bucket edge in floating point. */
const GROUP_EDGE_EPSILON = 1e-7

/**
 * The column on a grid `factor` times coarser: each grouped bucket [k·W, k·W + W) holds the sum of
 * the server buckets whose lower edge falls in it. bidStart/askStart stay the lower edges of the
 * buckets holding the best bid/ask, so `bucketAt` and the renderers read it like any column. When
 * the best bid and best ask land in one grouped bucket, that bucket stays the bids' and the asks
 * that share it move up one row, so the two sides never draw over each other.
 */
export function groupHeatmapColumn<T extends OrderbookHeatmapColumn>(column: T, factor: number): T {
  const w = column.bucketWidth
  if (!(factor > 1) || !(w > 0)) return column
  const width = w * factor
  const key = (price: number) => Math.floor(price / width + GROUP_EDGE_EPSILON)

  const bidTop = key(column.bidStart)
  const bids: number[] = []
  column.bids.forEach((size, i) => {
    const j = bidTop - key(column.bidStart - i * w)
    bids[j] = (bids[j] ?? 0) + size
  })

  const askBottom =
    column.bids.length > 0 ? Math.max(key(column.askStart), bidTop + 1) : key(column.askStart)
  const asks: number[] = []
  column.asks.forEach((size, i) => {
    const j = Math.max(0, key(column.askStart + i * w) - askBottom)
    asks[j] = (asks[j] ?? 0) + size
  })

  return {
    ...column,
    bucketWidth: width,
    bidStart: bidTop * width,
    askStart: askBottom * width,
    bids: Array.from(bids, (size) => size ?? 0),
    asks: Array.from(asks, (size) => size ?? 0),
  }
}

/**
 * Size filter — "where do big orders cluster": only levels resting between min and max are drawn.
 * Applied client-side; the server always sends the full book, so the hover readout can still show
 * every level. 0 means no bound on that side. Input ids are shared by the TV study and the Percept
 * control, and mirrored into chartOption (orderbookHeatmapMinSize/MaxSize).
 */
export const ORDERBOOK_HEATMAP_MIN_SIZE_INPUT_ID = 'minSize'
export const ORDERBOOK_HEATMAP_MAX_SIZE_INPUT_ID = 'maxSize'

export type HeatmapSizeFilter = { min: number; max: number }
export const NO_HEATMAP_SIZE_FILTER: HeatmapSizeFilter = { min: 0, max: 0 }

const SIZE_SUFFIXES: Record<string, number> = { k: 1e3, m: 1e6, b: 1e9 }

/**
 * A size bound from an input or config value. Accepts shorthand — "500k", "1.5m", "2b", "1,000,000"
 * — since book sizes run into the millions. Anything not a positive number is 0 (no bound).
 */
export function parseOrderbookHeatmapSize(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? value : 0
  if (typeof value !== 'string') return 0
  const match = /^([\d.]+)\s*([kmb])?$/i.exec(value.trim().replace(/,/g, ''))
  if (!match) return 0
  const size = Number(match[1]) * (match[2] ? SIZE_SUFFIXES[match[2].toLowerCase()] : 1)
  return Number.isFinite(size) && size > 0 ? size : 0
}

/** A size bound back in shorthand ("500K", "1.5M"), or '' for no bound. */
export function formatOrderbookHeatmapSize(size: number): string {
  if (!(size > 0)) return ''
  for (const [suffix, factor] of [
    ['B', 1e9],
    ['M', 1e6],
    ['K', 1e3],
  ] as const) {
    if (size >= factor) return `${Number((size / factor).toPrecision(4))}${suffix}`
  }
  return String(Number(size.toPrecision(6)))
}

/** Quick-add steps for the size filter, like the TWAP duration presets. */
export const ORDERBOOK_HEATMAP_SIZE_PRESETS = [10e3, 100e3, 500e3, 1e6, 5e6] as const

export function isHeatmapSizeFilterActive(filter: HeatmapSizeFilter): boolean {
  return filter.min > 0 || filter.max > 0
}

export function passesHeatmapSizeFilter(size: number, filter: HeatmapSizeFilter): boolean {
  return size >= filter.min && (filter.max <= 0 || size <= filter.max)
}

/**
 * The column with every level outside the size filter set to 0. Levels are zeroed, not removed,
 * since a level's price comes from its index.
 */
export function filterHeatmapColumnBySize<T extends OrderbookHeatmapColumn>(
  column: T,
  filter: HeatmapSizeFilter
): T {
  if (!isHeatmapSizeFilterActive(filter)) return column
  const keep = (size: number) => (passesHeatmapSizeFilter(size, filter) ? size : 0)
  return { ...column, bids: column.bids.map(keep), asks: column.asks.map(keep) }
}

/** Server cap on snapshots per request. */
export const ORDERBOOK_HEATMAP_MAX_COLUMNS = 1500

export type HeatmapSide = 'bid' | 'ask'

/** A column with its timestamp parsed once, rather than per frame. */
export type HeatmapColumn = OrderbookHeatmapColumn & { startMs: number }

export function toHeatmapColumns(columns: readonly OrderbookHeatmapColumn[]): HeatmapColumn[] {
  return columns.map((column) => ({ ...column, startMs: Number(column.tsMs) }))
}

/**
 * A live column (`OrderbookHeatmapStream`) placed on the bar holding its `tsMs`, or null when that
 * bar is not `openBarMs`: once its bar closes, that bar goes back to its stored opening snapshot.
 */
export function placeLiveColumn(
  column: OrderbookHeatmapColumn | null,
  timeframe: number,
  openBarMs: number
): HeatmapColumn | null {
  if (!column) return null
  const startMs = chartBarStartMs(Number(column.tsMs), timeframe)
  return startMs === openBarMs ? { ...column, startMs } : null
}

/**
 * `columns` with the open bar drawn from the live column: it replaces that bar's stored opening
 * snapshot, follows the newest stored column when the poll hasn't caught up to the bar yet, and is
 * ignored when the stored data already runs past it.
 */
export function withLiveColumn(
  columns: readonly HeatmapColumn[],
  live: HeatmapColumn | null
): readonly HeatmapColumn[] {
  const last = columns.at(-1)
  if (!live || (last && last.startMs > live.startMs)) return columns
  return last?.startMs === live.startMs ? [...columns.slice(0, -1), live] : [...columns, live]
}

/**
 * The request window for a visible chart range: snapped to whole bars so a pan within one bar
 * reuses the cached response, padded by half a screen on the left so a short pan back still has
 * columns, and capped at the server's snapshot limit. None until the chart has a visible range:
 * TV reports 0–0 before its bars load (e.g. right after a resolution change), and a window from
 * that starts a bar before the epoch, which the server rejects.
 */
export function buildHeatmapWindow(
  visibleRange: { from: number; to: number },
  timeframe: number
): { fromMs: number; toMs: number; limit: number } | null {
  if (visibleRange.to <= 0) return null
  const fromMs = visibleRange.from * 1000
  const toMs = visibleRange.to * 1000
  const snappedTo = nextChartBarStartMs(chartBarStartMs(toMs, timeframe), timeframe)
  const halfSpan = Math.max(toMs - fromMs, snappedTo - chartBarStartMs(toMs, timeframe)) / 2
  const snappedFrom = chartBarStartMs(fromMs - halfSpan, timeframe)
  const bars = Math.round(
    chartBarPosition(snappedTo, timeframe) - chartBarPosition(snappedFrom, timeframe)
  )
  const limit = Math.min(ORDERBOOK_HEATMAP_MAX_COLUMNS, Math.max(1, bars + 1))
  return {
    fromMs: Math.max(addChartBarsMs(snappedTo, -limit, timeframe), snappedFrom),
    toMs: snappedTo,
    limit,
  }
}

/**
 * Whether the fetched window `current` already spans `next` (same series) without being more than
 * twice its width, so a pan or zoom that stays inside it reuses the response instead of
 * refetching. Bar-snapping alone only reuses it for pans of under one bar.
 */
export function sameSeriesWindowCovers(
  current: OrderbookHeatmapRequest,
  next: OrderbookHeatmapRequest
): boolean {
  return (
    current.exchange === next.exchange &&
    current.symbol === next.symbol &&
    current.timeframe === next.timeframe &&
    Number(current.fromMs) <= Number(next.fromMs) &&
    Number(current.toMs) >= Number(next.toMs) &&
    (current.limit ?? 0) <= (next.limit ?? 0) * 2
  )
}

/** Index of the column whose bucket holds `timeMs`, or -1. Columns are oldest first. */
export function findColumnIndex(
  columns: readonly HeatmapColumn[],
  timeMs: number,
  timeframe: number
): number {
  let low = 0
  let high = columns.length - 1
  while (low <= high) {
    const middle = (low + high) >> 1
    if (columns[middle].startMs <= timeMs) low = middle + 1
    else high = middle - 1
  }
  const index = low - 1
  if (index < 0 || timeMs >= nextChartBarStartMs(columns[index].startMs, timeframe)) return -1
  return index
}

/** The bucket of `column` holding `price`: its side, lower edge and resting size (0 if empty). */
export function bucketAt(
  column: OrderbookHeatmapColumn,
  price: number
): { side: HeatmapSide; low: number; size: number } | null {
  const w = column.bucketWidth
  if (!(w > 0)) return null
  if (price >= column.askStart) {
    const i = Math.floor((price - column.askStart) / w)
    return { side: 'ask', low: column.askStart + i * w, size: column.asks[i] ?? 0 }
  }
  if (price < column.bidStart + w) {
    // bids[i] is [bidStart - i·w, bidStart - i·w + w), so a lower edge belongs to its own bucket.
    const i = Math.max(0, Math.ceil((column.bidStart - price) / w))
    return { side: 'bid', low: column.bidStart - i * w, size: column.bids[i] ?? 0 }
  }
  // Inside a spread wider than one bucket: no resting size.
  return { side: price >= column.midPrice ? 'ask' : 'bid', low: price, size: 0 }
}

/**
 * Quantity-based intensity in (0, 1], one scale for both sides — a level is coloured by how much
 * rests there, not by whether it's a bid or an ask. Anchored on the response's own size
 * percentiles, so it adapts to each book: under p70 isn't drawn, p70 starts at the ramp's blue
 * end, p85 is green and only p98 up is the top (yellow). Between anchors it runs on log size —
 * books are heavy-tailed, and linear steps put most of the tail at the top colour. Built once per
 * response from a bounded sample, then queried per cell; 0 means not drawn.
 */
export type HeatmapIntensityScale = (size: number) => number

const SCALE_SAMPLE_LIMIT = 20_000
/** [percentile, ramp position] pairs; 0.6 on the ramp is its green stop. */
const HEATMAP_ANCHORS = [
  [0.7, 0.001],
  [0.85, 0.6],
  [0.98, 1],
] as const

export function percentile(sorted: readonly number[], p: number): number {
  return sorted[Math.min(sorted.length - 1, Math.max(0, Math.floor(p * (sorted.length - 1))))]
}

/**
 * The size at each anchor percentile of an ascending sample, tie-aware: an anchor never lands on
 * a size shared by levels below the percentile, but on the next larger size (Infinity if none).
 * Market makers often ladder one size across the whole book (4.7K every $10); plain percentiles
 * then all equal that size and every level reads as a wall, where it is the background.
 */
export function heatmapAnchorSizes(sorted: readonly number[]): number[] {
  const n = sorted.length
  return HEATMAP_ANCHORS.map(([p]) => {
    let index = Math.min(n - 1, Math.ceil(p * n))
    // Back to the start of the run of equal sizes `index` sits in.
    let start = index
    while (start > 0 && sorted[start - 1] === sorted[index]) start--
    if (start < Math.ceil(p * n)) {
      // The run starts under the percentile: anchor on the first size above it.
      while (index < n && sorted[index] === sorted[start]) index++
      return index < n ? sorted[index] : Infinity
    }
    return sorted[index]
  })
}

/** A size's ramp position against `heatmapAnchorSizes`: 0 under the first, 1 from the last. */
export function anchoredIntensity(size: number, anchorSizes: readonly number[]): number {
  if (!(size > 0) || size < anchorSizes[0]) return 0
  for (let i = 1; i < anchorSizes.length; i++) {
    if (size < anchorSizes[i]) {
      const [low, high] = [anchorSizes[i - 1], anchorSizes[i]]
      const [tLow, tHigh] = [HEATMAP_ANCHORS[i - 1][1], HEATMAP_ANCHORS[i][1]]
      return Number.isFinite(high) && high > low
        ? tLow + ((tHigh - tLow) * Math.log(size / low)) / Math.log(high / low)
        : tLow
    }
  }
  return 1
}

/** A bounded, ascending sample of the positive sizes across `columns`, both sides. */
export function sampleHeatmapSizes(
  columns: readonly OrderbookHeatmapColumn[],
  filter: HeatmapSizeFilter = NO_HEATMAP_SIZE_FILTER
): number[] {
  const total = columns.reduce((sum, column) => sum + column.bids.length + column.asks.length, 0)
  const stride = Math.max(1, Math.ceil(total / SCALE_SAMPLE_LIMIT))
  const values: number[] = []
  let cursor = 0
  for (const column of columns) {
    for (const sizes of [column.bids, column.asks]) {
      for (const size of sizes) {
        if (cursor++ % stride === 0 && size > 0 && passesHeatmapSizeFilter(size, filter)) {
          values.push(size)
        }
      }
    }
  }
  return values.sort((a, b) => a - b)
}

export function buildIntensityScale(
  columns: readonly OrderbookHeatmapColumn[]
): HeatmapIntensityScale {
  const values = sampleHeatmapSizes(columns)
  if (values.length === 0) return () => 0
  const anchorSizes = heatmapAnchorSizes(values)
  return (size) => anchoredIntensity(size, anchorSizes)
}

/**
 * The heatmap's colour ramp: Percept's "Cyber" order (purple → teal → green → yellow) but muted —
 * the engine's own Cyber ends in pure #FFFF00, which glared. The dark ramp serves the Dark and
 * Blue themes; the light one swaps the yellow end for amber, which reads on white. Canvas fills and the Percept engine take literal
 * colours, so these are resolved per theme in JS like `useChartColors`.
 */
const HEATMAP_RAMP_STOPS = {
  // The weak end starts at a mid violet, not near-black: on the dark surfaces a deep purple at low
  // alpha read as black lines.
  dark: ['#6a55d0', '#4f74d6', '#2b9aa0', '#3fb37a', '#b5c95e', '#dcd67a'],
  // Light surfaces: the same violet → blue → teal → green order, ending in amber → orange since
  // yellow (and the old pale yellow-green start) vanishes on white.
  light: ['#8a7fdc', '#4f74d6', '#2b8f96', '#2f9a5f', '#d08a12', '#c2410c'],
} as const
const RAMP_ALPHA = { dark: [0.55, 0.9], light: [0.5, 0.9] } as const

type Rgba = [number, number, number, number]

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

/** The ramp's colour at position `t` in [0, 1]. */
function rampColorAt(t: number, isDark: boolean): Rgba {
  const theme = isDark ? 'dark' : 'light'
  const stops = HEATMAP_RAMP_STOPS[theme].map(hexToRgb)
  const [alphaLow, alphaHigh] = RAMP_ALPHA[theme]
  const clamped = Math.min(1, Math.max(0, t))
  const scaled = clamped * (stops.length - 1)
  const from = Math.min(stops.length - 2, Math.floor(scaled))
  const local = scaled - from
  const [r, g, b] = stops[from].map((c, k) => Math.round(c + (stops[from + 1][k] - c) * local))
  return [r, g, b, alphaLow + (alphaHigh - alphaLow) * clamped]
}

function rampRgba(steps: number, isDark: boolean): Rgba[] {
  return Array.from({ length: steps }, (_, i) =>
    rampColorAt(steps === 1 ? 1 : i / (steps - 1), isDark)
  )
}

/** `steps` canvas fill colours from weakest to strongest, alpha included. */
export function buildHeatmapRamp(steps: number, isDark: boolean): string[] {
  return rampRgba(steps, isDark).map(([r, g, b, a]) => `rgba(${r},${g},${b},${a.toFixed(3)})`)
}

/**
 * The ramp as a Percept colour scheme: `#rrggbbaa` stops led by a transparent one for sizes under
 * the threshold (the weakest colour at zero alpha). The engine spreads the stops evenly between its min and max thresholds, so
 * `positionAt(fraction)` (default: linear) says which ramp colour each fraction of that range gets
 * — how the anchored scale above is reproduced on an engine that only interpolates linearly.
 */
export function buildHeatmapRampHex(
  steps: number,
  isDark: boolean,
  positionAt: (fraction: number) => number = (fraction) => fraction
): string[] {
  const hex = (value: number) => value.toString(16).padStart(2, '0')
  // The transparent stop carries the weakest colour's RGB, not black: the engine blends between
  // neighbouring stops, and blending up from #00000000 drew the levels just over the threshold as
  // near-black lines.
  const [r0, g0, b0] = rampColorAt(positionAt(0), isDark)
  return [
    `#${hex(r0)}${hex(g0)}${hex(b0)}00`,
    ...Array.from({ length: steps }, (_, i) => {
      const [r, g, b, a] = rampColorAt(positionAt((i + 1) / steps), isDark)
      return `#${hex(r)}${hex(g)}${hex(b)}${hex(Math.round(a * 255))}`
    }),
  ]
}

/**
 * The IANA zone the chart's time axis is drawn in, so labels next to it read the same clock.
 * `'exchange'` defers to the symbol's zone; anything `Intl` can't format falls back to the
 * browser's (`undefined`).
 */
export function resolveChartTimeZone(
  timezone: { id: string; alias?: string } | undefined,
  symbolTimeZone: string | undefined
): string | undefined {
  const id = timezone?.alias ?? timezone?.id
  const zone = id === 'exchange' ? symbolTimeZone : id
  if (!zone) return undefined
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone })
    return zone
  } catch {
    return undefined
  }
}

/**
 * Time → x for a chart whose bars are evenly spaced, calibrated against one bar. Linear in bars,
 * not in time: a monthly bar is one bar spacing wide whether its month runs 28 days or 31.
 */
export function barTimeToX(
  referenceTimeMs: number,
  referenceCenterX: number,
  barSpacing: number,
  timeframe: number
): (timeMs: number) => number {
  const reference = chartBarPosition(referenceTimeMs, timeframe)
  return (timeMs) =>
    referenceCenterX + (chartBarPosition(timeMs, timeframe) - reference) * barSpacing
}

/**
 * Finds where one bar's slot starts on the time axis: `coordinateToTime` is a step function of
 * x, so bisect between a point known to be one bar to the left and `x` for the step into
 * `referenceTime`. The bar's centre is half a bar spacing to the right of that edge.
 */
export function calibrateBarCenter(
  coordinateToTime: (x: number) => number | null,
  x: number,
  barSpacing: number
): { timeMs: number; centerX: number } | null {
  const reference = coordinateToTime(x)
  if (reference === null) return null
  let inside = x
  let outside = x - barSpacing
  if (coordinateToTime(outside) === reference) return null
  for (let i = 0; i < 12 && inside - outside > 0.05; i++) {
    const middle = (inside + outside) / 2
    if (coordinateToTime(middle) === reference) inside = middle
    else outside = middle
  }
  return { timeMs: reference * 1000, centerX: inside + barSpacing / 2 }
}
